import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
  IconButton,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import { makeStyles } from "tss-react/mui";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { Colors } from "../../style/Colors";
import { IPomReportingTableData } from "./pom-reporting-table-data";
import { PomReportingTableQuantityColumn } from "./pom-reporting-table-quantity-column.component";
import { PomReportingHistoryRow } from "./pom-reporting-history-row.component";
import { formatQuantity } from "./reporting-quantity.helper";
import {
  formatLineTotal,
  formatMoney,
  formatUnitPriceOrPlaceholder,
  hasAnyPrice,
  hasVisibleChange,
  periodTotals,
} from "./reporting-price.helper";

/** Columns shown regardless of pricing: pos, item, description, weight class, unit, 2× quantity, history. */
const BASE_COLUMN_COUNT = 8;

const useStyles = makeStyles()({
  headerCell: {
    fontWeight: "bold",
    backgroundColor: Colors.grayLight,
  },
  headerCellNarrow: {
    fontWeight: "bold",
    backgroundColor: Colors.grayLight,
    whiteSpace: "nowrap",
    width: "1%",
  },
  cellNarrow: {
    whiteSpace: "nowrap",
    width: "1%",
  },
  emptyText: {
    textAlign: "center",
    padding: "40px",
    color: Colors.grayDark,
  },
  errorText: {
    textAlign: "center",
    padding: "40px",
    color: Colors.red,
  },
  changedRow: {
    backgroundColor: "#f0f9eb",
  },
  totalCell: {
    fontWeight: "bold",
    whiteSpace: "nowrap",
  },
  footerNote: {
    color: Colors.grayDark,
    borderBottom: "none",
  },
});

interface IPomReportingTableProps {
  positions: IPomReportingTableData[];
  disabled: boolean;
  onQuantityChange: (positionNo: number, quantity: number) => void;
  expandedPositionNos: Set<number>;
  onToggleHistory: (positionNo: number) => void;
  contractNo: string;
  reportingNo: number;
  isError: boolean;
}

export const PomReportingTable: FC<IPomReportingTableProps> = (props) => {
  const {
    positions,
    disabled,
    onQuantityChange,
    expandedPositionNos,
    onToggleHistory,
    contractNo,
    reportingNo,
    isError,
  } = props;
  const { classes } = useStyles();
  const { t } = useTranslation();

  // A load failure must not read as "nothing to report": that could cost the user a filing
  if (isError) {
    return <Typography className={classes.errorText}>{t("pom.reportings.table.load_error")}</Typography>;
  }

  if (positions.length === 0) {
    return <Typography className={classes.emptyText}>{t("pom.reportings.table.empty")}</Typography>;
  }

  // BC leaves prices empty until it resolves them, so an unpriced period skips a column of dashes
  const showPrices = hasAnyPrice(positions);
  const totals = periodTotals(positions);

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCellNarrow}>{t("pom.reportings.table.position_no")}</TableCell>
            <TableCell className={classes.headerCellNarrow}>{t("pom.reportings.table.item_no")}</TableCell>
            <TableCell className={classes.headerCell}>{t("pom.reportings.table.description")}</TableCell>
            <TableCell className={classes.headerCellNarrow}>{t("pom.reportings.table.weight_class")}</TableCell>
            <TableCell className={classes.headerCellNarrow}>{t("pom.reportings.table.unit_of_measure")}</TableCell>
            <TableCell className={classes.headerCellNarrow} align="right">
              {t("pom.reportings.table.current_quantity")}
            </TableCell>
            <TableCell className={classes.headerCellNarrow} align="right">
              {t("pom.reportings.table.quantity")}
            </TableCell>
            {showPrices && (
              <>
                <TableCell className={classes.headerCellNarrow} align="right">
                  {t("pom.reportings.table.unit_price")}
                </TableCell>
                <TableCell className={classes.headerCellNarrow} align="right">
                  {t("pom.reportings.table.line_total")}
                </TableCell>
              </>
            )}
            <TableCell className={classes.headerCellNarrow}>{t("pom.reportings.table.history")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {positions.map((position) => (
            <React.Fragment key={position.positionNo}>
              <TableRow
                className={position.editedQuantity !== undefined ? classes.changedRow : undefined}
              >
                <TableCell className={classes.cellNarrow}>{position.positionNo}</TableCell>
                <TableCell className={classes.cellNarrow}>{position.itemNo}</TableCell>
                <TableCell>{position.description}</TableCell>
                <TableCell className={classes.cellNarrow}>{position.weightClass ?? ""}</TableCell>
                <TableCell className={classes.cellNarrow}>{position.unit ?? ""}</TableCell>
                <TableCell className={classes.cellNarrow} align="right">
                  {formatQuantity(position.currentQuantity, position.unit)}
                </TableCell>
                <TableCell className={classes.cellNarrow} align="right">
                  <PomReportingTableQuantityColumn
                    positionNo={position.positionNo}
                    currentQuantity={position.currentQuantity}
                    editedQuantity={position.editedQuantity}
                    unit={position.unit}
                    disabled={disabled}
                    onQuantityChange={onQuantityChange}
                  />
                </TableCell>
                {showPrices && (
                  <>
                    <TableCell className={classes.cellNarrow} align="right">
                      {formatUnitPriceOrPlaceholder(position.unitPrice)}
                    </TableCell>
                    <TableCell className={classes.cellNarrow} align="right">
                      {formatLineTotal(position)}
                    </TableCell>
                  </>
                )}
                <TableCell className={classes.cellNarrow}>
                  <IconButton
                    size="small"
                    onClick={() => onToggleHistory(position.positionNo)}
                    color={expandedPositionNos.has(position.positionNo) ? "primary" : "default"}
                    title={t("pom.reportings.history.toggle_button_label")}
                  >
                    <HistoryIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
              {expandedPositionNos.has(position.positionNo) && (
                <PomReportingHistoryRow
                  contractNo={contractNo}
                  reportingNo={reportingNo}
                  positionNo={position.positionNo}
                  unit={position.unit}
                  colSpanTotal={showPrices ? BASE_COLUMN_COUNT + 2 : BASE_COLUMN_COUNT}
                />
              )}
            </React.Fragment>
          ))}
        </TableBody>
        {showPrices && (
          <TableFooter>
            <TableRow>
              <TableCell className={classes.totalCell} colSpan={BASE_COLUMN_COUNT} align="right">
                {t("pom.reportings.table.total_net")}
              </TableCell>
              <TableCell className={classes.totalCell} align="right">
                {formatMoney(totals.total)}
              </TableCell>
              <TableCell />
            </TableRow>
            {hasVisibleChange(totals.changeTotal) && (
              <TableRow>
                <TableCell className={classes.totalCell} colSpan={BASE_COLUMN_COUNT} align="right">
                  {t("pom.reportings.table.total_change")}
                </TableCell>
                <TableCell className={classes.totalCell} align="right">
                  {formatMoney(totals.changeTotal)}
                </TableCell>
                <TableCell />
              </TableRow>
            )}
            {totals.unpricedCount > 0 && (
              <TableRow>
                <TableCell className={classes.footerNote} colSpan={BASE_COLUMN_COUNT + 2}>
                  {t("pom.reportings.table.unpriced_note", { count: totals.unpricedCount })}
                </TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell className={classes.footerNote} colSpan={BASE_COLUMN_COUNT + 2}>
                {t("pom.reportings.table.vat_note")}
              </TableCell>
            </TableRow>
          </TableFooter>
        )}
      </Table>
    </TableContainer>
  );
};
